import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Row,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { hexToColorOption } from '../../../utils/colorUtils';
import { useTeachingBlockStore } from '../../TeachingBlockGrid/Contexts/TeachingBlockContext';
import { TeachingBlockVersionTableType } from '../graphql/types';
import { createTeachingBlockVersionTableColumns } from '../utils/TeachingBlockVersionTableUtils';

type TeachingBlockVersionTeachersTableProps = {
  versionUuid: string | undefined;
  search: string;
  versionTeacherUuids: string[];
  onRowSelection: (rowData: TeachingBlockVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
  onRemove: (uuids: string[]) => void | Promise<void>;
};

export const TeachingBlockVersionTeachersTable = observer(
  ({
    versionTeacherUuids,
    versionUuid,
    search,
    onRowSelection,
    onShowLessonClick,
    onRemove,
  }: TeachingBlockVersionTeachersTableProps) => {
    const { t } = useTranslation();

    const { sorting, saveSorting } = useColumnsSort('teachingBlockVersionTeachersTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

    const columns = useMemo(() => createTeachingBlockVersionTableColumns(t('persons.name')), [t]);

    const store = useTeachingBlockStore();
    const { confirm, ConfirmationDialog } = useConfirm();

    // Lehrer, die noch auf einer (platzierten oder freien) Karte liegen, dürfen nicht entfernt werden.
    const usedTeachers = useMemo(
      () => [
        ...store.placedCards.flatMap((c) => c.teachers.map((teacher) => teacher.uuid)),
        ...store.freeCards.flatMap((c) => c.teachers.map((teacher) => teacher.uuid)),
      ],
      [store.placedCards, store.freeCards],
    );

    const handleRemove = useCallback(
      async (uuids: string[]) => {
        const res = await confirm({
          title: t('timetableVersion.removeTeachers', { count: uuids.length }),
          message: t('timetableVersion.removeTeachersDescription', { count: uuids.length }),
        });
        if (res) {
          await onRemove(uuids);
        }
      },
      [confirm, onRemove, t],
    );

    const createActionItems = useCallback(
      (row: Row<TeachingBlockVersionTableType>): DropdownMenuItem[] => {
        return [
          {
            label: t('teachingBlockVersion.remove'),
            type: 'default',
            color: 'error',
            disabled: store.readonly || usedTeachers.includes(row.original.uuid),
            onClick: async () => {
              await handleRemove([row.original.uuid]);
            },
          },
        ];
      },
      [handleRemove, t, store.readonly, usedTeachers],
    );

    const teacherTableData: TeachingBlockVersionTableType[] = [];
    store.teachers.forEach((t) => {
      const { html, label } = hexToColorOption(t.timetableConfig?.color ?? '');
      teacherTableData.push({
        color: {
          color: html,
          colorLabel: label,
        },
        count: 0,
        name: t.displayNameShort ?? t.fullName,
        shortName: t.shortName,
        uuid: t.uuid,
        active: t.active,
      });
    });

    return (
      <>
        <Table<TeachingBlockVersionTableType>
          globalFilter={search}
          showBorderRadius
          showShadow
          canScroll
          minHeight={900}
          sorting={sorting}
          onSortingChange={saveSorting}
          columns={columns}
          data={teacherTableData}
          lastColWidth='140px'
          lastCol={(row) => {
            return (
              <ButtonGroup>
                <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                  {t('teachingBlockVersion.showLessons')}
                </Button>
                <Dropdown
                  noPadding
                  disabled={store.readonly}
                  trigger={
                    <Button
                      disabled={store.readonly}
                      hierarchy='secondary'
                      icon={<DotsHorizontalIcon className='small' />}
                    />
                  }
                >
                  <DropdownMenu data={createActionItems(row)} />
                </Dropdown>
              </ButtonGroup>
            );
          }}
          showRowHover
          onRowClick={(event, row) => {
            if (onRowSelectionChange) onRowSelectionChange({ [row.id]: true });
            onRowSelection(row.original, row.index);
            document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          rowSelection={rowSelection}
          showSelected
        />
        <ConfirmationDialog />
      </>
    );
  },
);
